import "../style.scss";
import "./StatementI18n/style.scss";

import { json } from "@codemirror/lang-json";
import { vscodeLight } from "@uiw/codemirror-theme-vscode";
import CodeMirror from "@uiw/react-codemirror";
import Checkmark from "assets/img/svg/checkmark";
import classNames from "classnames";
import ControlFilter from "components/shared/ControlFilter";
import StyledButton from "components/shared/StyledButton";
import { mergeDeep } from "helpers/utils";
import { useCompanySettings } from "hooks/useCompanySettings";
import { Languages } from "languages";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { putCompanySettings } from "store/settingsStore";
import { useAppDispatch, useAppSelector } from "store/store";

type Lang = "fr" | "en";

const jsonExtensions = [json()];

const WordingForm = ({ lang }: { lang: Lang }) => {
    const dispatch = useAppDispatch();
    const [t] = useTranslation();
    const { availableStatementI18nKeys, availableStatementI18nKeysEn } = useAppSelector((state) => state.settings);
    const { getCompanySettings } = useCompanySettings();
    const [submitted, setSubmitted] = useState(false);
    const [initialized, setInitialized] = useState(false);

    const settingKey = lang === "en" ? "custom_statement_wording_en" : "custom_statement_wording";
    const baseKeys = lang === "en" ? availableStatementI18nKeysEn : availableStatementI18nKeys;
    const customWording = getCompanySettings(settingKey);

    const {
        control,
        handleSubmit,
        reset,
        formState: { errors, isDirty },
    } = useForm({
        defaultValues: { customCompanyKeys: "" },
        mode: "all",
    });

    useEffect(() => {
        if (!initialized && baseKeys && Object.keys(baseKeys).length > 0) {
            const clone = mergeDeep({}, baseKeys);
            const merged = customWording ? mergeDeep(clone, JSON.parse(customWording)) : clone;
            reset({ customCompanyKeys: JSON.stringify(merged, null, "\t") });
            setInitialized(true);
        }
    }, [initialized, baseKeys, customWording, reset]);

    const onSubmit = (formData: { customCompanyKeys: string }) => {
        setSubmitted(true);
        dispatch(putCompanySettings([{ settingsName: settingKey, settingsValue: formData.customCompanyKeys }])).then(
            () => {
                setSubmitted(false);
                reset({ customCompanyKeys: formData.customCompanyKeys });
            },
        );
    };

    const hasJsonError = !!errors.customCompanyKeys;
    const showFloatingSave = isDirty && !hasJsonError;

    return (
        <div className="statement-i18n__content">
            <form id="statement-i18n-form" className="statement-i18n__form" onSubmit={handleSubmit(onSubmit)}>
                <span className="statement-i18n__label">Valeurs personnalisées</span>
                <Controller
                    name="customCompanyKeys"
                    control={control}
                    rules={{
                        required: t("NewElement.Required"),
                        validate: (content: string) => {
                            try {
                                JSON.parse(content);
                                return true;
                            } catch {
                                return "Le contenu n'est pas une valeur JSON correcte";
                            }
                        },
                    }}
                    render={({ field }) => (
                        <div
                            className={classNames("statement-i18n__editor", {
                                "has-error": hasJsonError,
                            })}
                        >
                            <CodeMirror
                                value={field.value}
                                theme={vscodeLight}
                                extensions={jsonExtensions}
                                onChange={field.onChange}
                                basicSetup={{
                                    lineNumbers: true,
                                    foldGutter: true,
                                    highlightActiveLine: true,
                                    indentOnInput: true,
                                    bracketMatching: true,
                                    closeBrackets: true,
                                }}
                            />
                        </div>
                    )}
                />
                {hasJsonError && <span className="statement-i18n__error">{errors.customCompanyKeys?.message}</span>}
            </form>
            {showFloatingSave && (
                <div className="statement-i18n__floating-save">
                    <StyledButton
                        type="submit"
                        form="statement-i18n-form"
                        buttonType="primary"
                        className="statement-i18n__floating-save-btn"
                        loading={submitted}
                        disabled={submitted}
                    >
                        {!submitted && (
                            <>
                                <Checkmark /> {t("AdminPanel.ValidateStatementChanges")}
                            </>
                        )}
                    </StyledButton>
                </div>
            )}
        </div>
    );
};

const StatementI18n = () => {
    const [lang, setLang] = useState<Lang>("fr");

    const languageOptions = [
        { value: Languages()[0].value, label: Languages()[0].description },
        { value: Languages()[1].value, label: Languages()[1].description },
    ];

    return (
        <div className="statement-i18n">
            <ControlFilter
                filterOptions={languageOptions}
                controlValue={lang}
                onValueChange={(value) => setLang(value)}
            />
            <WordingForm key={lang} lang={lang} />
        </div>
    );
};

export default StatementI18n;
